import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Eye, Check, Trash2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { DataState } from '@/components/DataState';

function getMatchBadgeClass(matchType) {
  if (matchType === 'exact_invoice') return 'bg-destructive/20 text-destructive';
  if (matchType === 'fuzzy_invoice') return 'bg-amber-500/20 text-amber-500';
  return 'bg-blue-500/20 text-blue-400';
}

function getMatchLabel(matchType, confidence) {
  if (matchType === 'exact_invoice') return 'Exact Match';
  const pct = Math.round((confidence || 0) * 100);
  if (matchType === 'fuzzy_invoice') return `Fuzzy Match (${pct}%)`;
  return `Field Match (${pct}%)`;
}

function formatSender(sender, senderName) {
  if (!sender) return 'N/A';
  if (sender.startsWith('upload:')) {
    const parts = sender.split(':');
    const name = parts[1] || '';
    const email = parts[2] || '';
    return `Upload — ${name}${email ? ` (${email})` : ''}`;
  }
  if (sender === 'manual_upload') return 'Upload';
  const phone = sender.replace('whatsapp:', '');
  if (senderName) return `${senderName} ${phone}`;
  return phone;
}

export default function DuplicatesPage() {
  const [duplicates, setDuplicates] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [resolveGroup, setResolveGroup] = useState(null);
  const [keepId, setKeepId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      const { data } = await api.get(`/duplicates?${params}`);
      setDuplicates(data.duplicates);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { void 0; }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { fetchDuplicates(); }, [fetchDuplicates]);

  const handleResolve = async () => {
    if (!keepId || !resolveGroup) return;
    const deleteIds = resolveGroup.receipts
      .map(r => r.receipt_id)
      .filter(id => id !== keepId);
    try {
      await api.post('/duplicates/resolve', { keep_receipt_id: keepId, delete_receipt_ids: deleteIds });
      toast.success(`Resolved: kept 1, removed ${deleteIds.length} duplicate(s)`);
      setResolveGroup(null);
      setKeepId(null);
      fetchDuplicates();
    } catch {
      toast.error('Failed to resolve');
    }
  };

  const handlePreview = async (fileId, title) => {
    if (!fileId) return;
    try {
      const response = await api.get(`/files/${fileId}/preview`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      setPreviewUrl(window.URL.createObjectURL(blob));
      setPreviewTitle(title || 'Preview');
    } catch {
      toast.error('Preview failed');
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewTitle('');
  };

  const openResolveDialog = (group) => {
    setResolveGroup(group);
    setKeepId(group.receipts[0]?.receipt_id || null);
  };

  return (
    <div className="space-y-6" data-testid="duplicates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Duplicates
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {total} duplicate invoice group{total !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchDuplicates(); }} className="flex gap-3" data-testid="duplicates-filters">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoice number or company..."
            className="pl-10 h-10 rounded-[4px]"
            data-testid="duplicates-search-input"
          />
        </div>
        <Button type="submit" variant="outline" className="h-10 rounded-[4px]" data-testid="duplicates-filter-btn">Search</Button>
      </form>

      {/* Duplicate Groups */}
      <DataState
        loading={loading}
        empty={duplicates.length === 0}
        emptyContent={
          <div className="border border-border rounded-md p-12 text-center" data-testid="no-duplicates">
            <Check className="w-8 h-8 mx-auto mb-3 text-[hsl(var(--success))]" />
            <p className="text-sm font-medium">No duplicates found</p>
            <p className="text-xs text-muted-foreground mt-1">All invoice numbers are unique</p>
          </div>
        }
      >
        <div className="space-y-4" data-testid="duplicates-list">
          {duplicates.map((group) => (
            <div key={group.invoice_number} className="border border-border rounded-md overflow-hidden" data-testid={`dupe-group-${group.invoice_number}`}>
              {/* Group Header */}
              <div className="flex items-center justify-between px-5 py-3 bg-secondary/30 border-b border-border">
                <div className="flex items-center gap-3 min-w-0">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-mono font-semibold">{group.invoice_number}</span>
                    <span className="text-xs text-muted-foreground ml-3">{group.count} copies</span>
                    <span className={`ml-3 inline-flex px-2 py-0.5 text-xs font-medium rounded ${getMatchBadgeClass(group.match_type)}`}>
                      {getMatchLabel(group.match_type, group.confidence)}
                    </span>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-[4px] shrink-0 ml-3" onClick={() => openResolveDialog(group)} data-testid={`resolve-btn-${group.invoice_number}`}>
                  Resolve
                </Button>
              </div>

              {/* Receipts in group */}
              <div className="divide-y divide-border">
                {group.receipts.map((r) => (
                  <div key={r.receipt_id} className="flex items-center justify-between px-5 py-3 hover:bg-secondary/20 transition-colors">
                    <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-1">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Invoice #</p>
                        <p className="text-sm font-mono">{r.invoice_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Company</p>
                        <p className="text-sm font-medium truncate">{r.company || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Date</p>
                        <p className="text-sm font-mono">{r.invoice_date || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                        <p className="text-sm font-mono font-semibold">{r.total || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Source</p>
                        <p className="text-sm">{formatSender(r.sender, r.sender_name)}</p>
                      </div>
                    </div>
                    {r.file_id && (
                      <Button variant="ghost" size="icon" className="w-7 h-7 shrink-0 ml-2" onClick={() => handlePreview(r.file_id, `${r.company} — ${r.invoice_date}`)} data-testid={`preview-dupe-${r.receipt_id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DataState>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between" data-testid="duplicates-pagination">
          <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="w-7 h-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="w-7 h-7" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Resolve Dialog */}
      <Dialog open={!!resolveGroup} onOpenChange={(open) => { if (!open) { setResolveGroup(null); setKeepId(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Resolve Duplicate: {resolveGroup?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            Select which receipt to keep. The others will be deleted along with their files.
          </p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {resolveGroup?.receipts.map((r) => (
              <div
                key={r.receipt_id}
                className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  keepId === r.receipt_id ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10' : 'border-border hover:bg-secondary/30'
                }`}
                onClick={() => setKeepId(r.receipt_id)}
                data-testid={`keep-option-${r.receipt_id}`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  keepId === r.receipt_id ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]' : 'border-muted-foreground'
                }`}>
                  {keepId === r.receipt_id && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{r.company || 'N/A'}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {r.invoice_date} &middot; {r.total} &middot; {r.sender === 'manual_upload' ? 'Upload' : 'WhatsApp'}
                  </p>
                </div>
                {keepId !== r.receipt_id && (
                  <Trash2 className="w-3.5 h-3.5 text-destructive shrink-0" />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => { setResolveGroup(null); setKeepId(null); }} className="rounded-[4px]" data-testid="resolve-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleResolve} disabled={!keepId} className="rounded-[4px]" data-testid="resolve-confirm-btn">
              Keep Selected, Delete Rest
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-testid="dupe-preview-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{previewTitle}</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="flex justify-center py-2">
              <img src={previewUrl} alt="Receipt" className="max-h-[600px] w-auto object-contain rounded" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
